import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { paginateData, calculateTotalPages } from "../../../assets/js/script";
import TableHeader from "../../Common/TableComponent/TableHeader";
import EntriesDropdown from "../../Common/TableComponent/EntriesDropdown";
import TablesRow from "../../Common/TableComponent/TablesRow";
import { Pagination } from "../../Common/TableComponent/Pagination";
import {
  fetchProductsVariants,
  deleteProductVariant,
} from "../../../services/productVariantService";
import { Loading } from "../../Common/OtherElements/Loading";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { confirmDelete } from "../../Common/OtherElements/confirmDeleteClone";
import { TableDataStatusError } from "../../Common/OtherElements/TableDataStatusError";
import { handleErrors } from "../../../utils/errorHandler";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import ProductVariantToggleSwitch from "./ProductVariantToggleSwitch";
import ProductVariantFeaturedToggleSwitch from "./ProductVariantFeaturedToggleSwitch";
import ProductVariantStockToggleSwitch from "./ProductVariantStockToggleSwitch";
import ProductVariantBestSellerToggleSwitch from "./ProductVariantBestSellerToggleSwitch";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";

const ViewProductVariant = () => {
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [manageProducts, setManageProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageAccessDetails, setPageAccessDetails] = useState([]);
  const PageLevelAccessurl = "category";
  const navigate = useNavigate();
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    } else {
      console.log("No page access details found");
    }
  }, [pageAccessData, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const response = await fetchProductsVariants();
        if (response.isSuccess) {
          setManageProducts(response.result);
        }
      } catch (error) {
        handleErrors(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredData = manageProducts.filter(
    (products) =>
      products.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      products.categoryName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      products.subCategoryName
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      products.brandName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      products.productSize.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const currentData = paginateData(filteredData, currentPage, entriesPerPage);
  const totalPages = calculateTotalPages(filteredData.length, entriesPerPage);

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage);

  const handleDelete = async (productId) => {
    const confirmed = await confirmDelete("product variant");
    if (confirmed) {
      try {
        await deleteProductVariant(productId);
        setManageProducts((prev) =>
          prev.filter((item) => item.id !== productId)
        );
        Swal.fire(
          "Deleted!",
          "Product Variant deleted successfully!",
          "success"
        );
      } catch (error) {
        handleErrors(error);
      }
    }
  };

  const handleStatusChange = (id, newStatus) => {
    setManageProducts((prevProperties) =>
      prevProperties.map((property) =>
        property.id === id ? { ...property, status: newStatus } : property
      )
    );
  };

  return (
    <>
      <ToastContainer />
      <ComponentHeader title="View Products Variants" />
      {pageAccessDetails.viewAccess ? (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5">
              <div className="card-header">
                <h5 className="mb-sm-2 mt-sm-2">Manage Product Variants</h5>
              </div>
              <div className="card-body manage-amenity-master-card-body">
                <div className="pagination-details-responsive justify-content-between mb-3">
                  <EntriesDropdown
                    entriesPerPage={entriesPerPage}
                    onEntriesChange={handleEntriesChange}
                    options={[10, 25, 50, 100]}
                  />
                  <div>
                    <input
                      type="text"
                      placeholder="Search..."
                      className="form-control mb-2"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>
                {loading ? (
                  <Loading />
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-bordered">
                      <TableHeader
                        columns={[
                          "#",
                          "Catalogue",
                          "Product",
                          "Published?",
                          "Featured?",
                          "InStock?",
                          "BestSeller?",
                          "Modified On",
                          "Action",
                        ]}
                      />
                      <tbody className="manage-property-owner-table-values">
                        {currentData.length === 0 ? (
                          <TableDataStatusError colspan="8" />
                        ) : (
                          currentData.map((item, index) => (
                            <TablesRow
                              key={item.id}
                              rowData={{
                                Id:
                                  (currentPage - 1) * entriesPerPage +
                                  index +
                                  1,
                                catalogue: (
                                  <div className=" p-2 fs-12">
                                    <span className="text-start d-block mb-2">
                                      <strong>Brand:</strong> {item.brandName}
                                    </span>
                                    <span className="text-start d-block mb-2">
                                      <strong>Category:</strong>{" "}
                                      {item.categoryName}
                                    </span>
                                    <span className="text-start d-block">
                                      <strong>SubCategory:</strong>{" "}
                                      {item.subCategoryName}
                                    </span>
                                  </div>
                                ),
                                productName: (
                                  <div className=" p-2 fs-12">
                                    <span className="text-start d-block mb-2">
                                      <strong>Product:</strong>{" "}
                                      {item.productName}
                                    </span>
                                    <span className="text-start d-block mb-2">
                                      <strong>Size:</strong> {item.productSize}
                                    </span>
                                    <span className="text-start d-block mb-2">
                                      <strong>Actual Price:</strong>{" "}
                                      {item.actualPrice}
                                    </span>
                                    <span className="text-start d-block">
                                      <strong>Discount Price:</strong>{" "}
                                      {item.discountPrice}
                                    </span>
                                  </div>
                                ),
                                publishOrUnPublish: (
                                  <ProductVariantToggleSwitch
                                    propId={item.id}
                                    initialStatus={
                                      item.published === true
                                        ? "Published"
                                        : "UnPublished"
                                    }
                                    onStatusChange={(newStatus) =>
                                      handleStatusChange(item.id, newStatus)
                                    }
                                  />
                                ),
                                featuredOrUnFeatured: (
                                  <ProductVariantFeaturedToggleSwitch
                                    propId={item.id}
                                    initialStatus={
                                      item.featured === true
                                        ? "Featured"
                                        : "UnFeatured"
                                    }
                                    onStatusChange={(newStatus) =>
                                      handleStatusChange(item.id, newStatus)
                                    }
                                  />
                                ),
                                inStockOrOutOffStock: (
                                  <ProductVariantStockToggleSwitch
                                    propId={item.id}
                                    initialStatus={
                                      item.inStock === true
                                        ? "In-Stock"
                                        : "Out-off-Stock"
                                    }
                                    onStatusChange={(newStatus) =>
                                      handleStatusChange(item.id, newStatus)
                                    }
                                  />
                                ),
                                bestSellerOrNotaBestSeller: (
                                  <ProductVariantBestSellerToggleSwitch
                                    propId={item.id}
                                    initialStatus={
                                      item.bestSeller === true
                                        ? "Best-Seller"
                                        : "Not-a-Best-Seller"
                                    }
                                    onStatusChange={(newStatus) =>
                                      handleStatusChange(item.id, newStatus)
                                    }
                                  />
                                ),

                                modifiedOn: (
                                  <div>
                                    {new Date(item.modifiedOn)
                                      .toLocaleString("en-GB", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        second: "2-digit",
                                        hour12: true,
                                      })
                                      .replace(",", "")}
                                    <br />
                                    <span className="text-muted fs-10">
                                      modified by {item.modifiedByUserName}
                                    </span>
                                  </div>
                                ),
                              }}
                              columns={[
                                "Id",
                                "catalogue",
                                "productName",
                                "publishOrUnPublish",
                                "featuredOrUnFeatured",
                                "inStockOrOutOffStock",
                                "bestSellerOrNotaBestSeller",
                                "modifiedOn",
                              ]}
                              hideIcons={false}
                              onEdit={() =>
                                navigate(`/product/${item.productId}/variant`)
                              }
                              onDelete={() => handleDelete(item.id)}
                              pageLevelAccessData={pageAccessDetails}
                            />
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalEntries={filteredData.length}
                  entriesPerPage={entriesPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </>
  );
};

export default ViewProductVariant;
